import { Button, Spinner, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { api, isApiError } from '~/api/client'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { useT } from '~/i18n/use-t'

const goTo = (url: string) => window.location.assign(url)

/**
 * Only an https address (the provider's own page) is followed. The one exception is our own origin: the development
 * fake provider's page lives on the app itself, which is plain http when it runs on localhost (dev and E2E).
 */
function safeUrl(url: string): string {
  const u = new URL(url)
  if (u.protocol !== 'https:' && u.origin !== window.location.origin) throw new Error('unsafe_redirect')
  return u.href
}

/**
 * Hosted redirect (copy v8 "provider page works on a computer"): starts the attempt with the consent, then sends
 * the browser to the provider's page. The member comes back to /join/return.
 */
export function HostedHandoff({ assign = goTo }: { assign?: (url: string) => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const started = useRef(false)
  const start = useMutation({
    mutationFn: async () => {
      const res = await api('attemptStart', { body: { consentBiometric: true } })
      if (res.kind !== 'redirect') throw new Error('unexpected_flow')
      assign(safeUrl(res.url))
    },
  })
  const { mutate } = start
  useEffect(() => {
    if (started.current) return
    started.current = true
    mutate()
  }, [mutate])

  return (
    <Stack gap={4} role='status' aria-live='polite'>
      {start.isError ? (
        <>
          <ErrorAlert>{friendly(start.error)}</ErrorAlert>
          {/* A person verifies this member by video call: trying again cannot change that. */}
          {!isApiError(start.error, 'manual_route') && (
            <Button alignSelf='flex-start' onClick={() => start.mutate()}>
              {t('common.tryAgain')}
            </Button>
          )}
        </>
      ) : (
        <>
          <Spinner aria-hidden />
          <Text>{t('face.opening')}</Text>
        </>
      )}
    </Stack>
  )
}
